import {
  ChatCircleIcon as ChatCircle,
  FileCodeIcon as FileCode,
  ArrowClockwiseIcon as ArrowClockwise,
  WarningIcon as Warning,
  DownloadSimpleIcon as DownloadSimple,
} from "@phosphor-icons/react";
import AgentIcon from "@/components/AgentIcon";
import Markdown from "@/components/Markdown";
import { logTelemetryEvent } from "@/lib/api";
import { ambiguityChips, compareSummary, objectContextNote } from "@/lib/objectContext";
import { CompareSummaryPanel } from "./CompareSummaryPanel";

const SAMPLE_PROMPTS = {
  architect: [
    "We run Infor XA on IBM i and our CFO wants to retire it. How do I evaluate that decision?",
    "Build me a phased modernization roadmap for a 30-year-old order management RPG suite.",
    "What's the minimum-investment option to expose our IBM i to a customer portal?",
    "Draft an executive summary for adding AI agents around our legacy distribution system.",
  ],
  "code-analyst": [
    "Review this RPG III program and tell me what it does and how to modernize it.",
    "Convert this fixed-format RPG to free-format RPGLE with ILE service program extraction.",
    "Find performance issues in this CLLE that orchestrates nightly batch jobs.",
    "Identify all the files and called programs in this RPG and produce a dependency table.",
  ],
  "erp-analyst": [
    "Walk me through order-to-cash in Infor XA on IBM i — files, programs, integration points.",
    "We're on BPCS. What's a realistic modernization path that preserves manufacturing knowledge?",
    "Compare JD Edwards World vs. EnterpriseOne for distribution modernization.",
    "What questions should I ask before recommending an ERP replacement?",
  ],
  integration: [
    "Design an API contract to expose customer master from DB2 for i to a web portal.",
    "Compare REST vs. event-driven integration for our IBM i order capture.",
    "Write an OpenAPI spec for our inventory lookup and reservation endpoints.",
    "How do I do change-data-capture from IBM i journals into Kafka?",
  ],
  agentic: [
    "Where can AI agents add value around our legacy IBM i ERP today?",
    "Design an agent that triages exceptions from our nightly batch jobs.",
    "What's the governance model for an agent that can read DB2 for i but never write?",
    "AI readiness assessment for an IBM i shop that wants to start with a copilot.",
  ],
  forensics: [
    "Records in our customer master are being updated overnight by nobody. Where do I look?",
    "Performance regression on a 20-year-old RPG after PTF — diagnostic plan?",
    "How do I use QAUDJRN and DSPJRN to track who changed pricing last weekend?",
    "Ghost duplicate orders appearing in our order file — forensic approach.",
  ],
};

export default function MessageList({
  scrollRef,
  loadingSession,
  messages,
  agent,
  onResume,
  resuming,
  onSamplePrompt,
  onPickAmbiguityChip,
  workspaceId,
}) {
  return (
    <div
      ref={scrollRef}
      className="flex-1 overflow-y-auto p-5 lg:p-8 space-y-6 bg-white"
      data-testid="chat-messages"
    >
      {loadingSession ? (
        <div className="text-center text-[var(--ml-text-secondary)] py-12">
          Loading...
        </div>
      ) : messages.length === 0 ? (
        <EmptyChat agent={agent} onSamplePrompt={onSamplePrompt} />
      ) : (
        messages.map((m) => (
          <MessageBubble
            key={m.id}
            message={m}
            agent={agent}
            onResume={
              m.role === "assistant" && m.truncated ? onResume : null
            }
            resuming={resuming}
            onPickAmbiguityChip={onPickAmbiguityChip}
            workspaceId={workspaceId}
          />
        ))
      )}
    </div>
  );
}

function EmptyChat({ agent, onSamplePrompt }) {
  const samples = SAMPLE_PROMPTS[agent.id] || SAMPLE_PROMPTS.architect;
  const isCodeAnalyst = agent.id === "code-analyst";
  return (
    <div
      className="h-full flex flex-col items-start justify-center py-8"
      data-testid="empty-chat"
    >
      <div className="flex items-center gap-3 mb-4">
        <ChatCircle size={20} color="#0f62fe" weight="duotone" />
        <div className="ml-mono text-xs uppercase tracking-widest text-[var(--ml-text-secondary)]">
          Start a conversation with {agent.name}
        </div>
      </div>
      <p className="text-[var(--ml-text-secondary)] max-w-2xl mb-6 leading-relaxed">
        {agent.tagline}
      </p>
      <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mb-2">
        Try one of these
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 w-full max-w-3xl">
        {samples.map((s) => (
          <SamplePromptButton key={s} text={s} onPick={onSamplePrompt} />
        ))}
      </div>
      {isCodeAnalyst && (
        <div
          className="mt-6 w-full max-w-3xl border-l-2 border-[var(--ml-primary)] bg-[var(--ml-surface)] px-4 py-3 flex items-start justify-between gap-4"
          data-testid="sample-pack-cta"
        >
          <div className="min-w-0">
            <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-primary)] mb-0.5">
              60-second demo
            </div>
            <div className="text-sm font-medium leading-snug">
              Don&apos;t have IBM i source handy? Download the sample pack — 3
              anonymised members (RPG, CL, DDS) ready to drop into the
              composer.
            </div>
          </div>
          <a
            href="/modernization-lab-sample-pack.zip"
            download="modernization-lab-sample-pack.zip"
            onClick={() => {
              logTelemetryEvent("sample_pack_downloaded");
            }}
            className="ml-btn-secondary shrink-0 flex items-center gap-2"
            data-testid="sample-pack-download-btn"
          >
            <DownloadSimple size={14} weight="bold" />
            Sample pack
          </a>
        </div>
      )}
    </div>
  );
}

function SamplePromptButton({ text, onPick }) {
  return (
    <button
      onClick={() => onPick?.(text)}
      className="text-left text-sm border border-[var(--ml-border)] bg-white hover:border-[var(--ml-text-primary)] hover:bg-[var(--ml-surface)] p-3 transition-colors"
      data-testid="sample-prompt"
    >
      {text}
    </button>
  );
}

// Iter 59 — clickable candidates for an ambiguous object name. Clicking prepares the
// clarified, library-qualified question in the composer (never auto-submits).
function AmbiguityChips({ chips, onPick }) {
  return (
    <div
      className="mb-3 flex flex-wrap gap-2 max-w-full"
      role="group"
      aria-label="Ambiguous object name — choose the exact object"
      data-testid="ambiguity-chips"
    >
      {chips.chips.map((c) => (
        <button
          key={c.key}
          type="button"
          onClick={() => onPick?.(c.question)}
          title={c.title}
          aria-label={c.aria}
          className="ml-mono text-[11px] max-w-full truncate border border-[var(--ml-border)] bg-[var(--ml-bg-secondary)] px-2.5 py-1 text-left text-[var(--ml-text-primary)] hover:border-[var(--ml-blue)] hover:text-[var(--ml-blue)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ml-blue)] transition-colors"
          data-testid="ambiguity-chip"
        >
          {c.text}
        </button>
      ))}
      {chips.moreText && (
        <span className="ml-mono text-[10px] self-center text-[var(--ml-text-secondary)]" data-testid="ambiguity-more">
          {chips.moreText}
        </span>
      )}
    </div>
  );
}

function MessageBubble({ message, agent, onResume, resuming, onPickAmbiguityChip, workspaceId }) {
  const isUser = message.role === "user";
  const isTruncated = !isUser && message.truncated;
  const objectNote = isUser ? null : objectContextNote(message.object_context);
  const chips = isUser ? null : ambiguityChips(message.object_context);
  const summary = isUser ? null : compareSummary(message.object_context, { workspaceId }); // Iter 65 — drawer links need the session workspace
  return (
    <div
      className="flex gap-3 lg:gap-4 ml-anim-in"
      data-testid={`message-${message.role}`}
    >
      <div
        className="w-8 h-8 shrink-0 flex items-center justify-center mt-0.5"
        style={isUser ? { background: "#161616" } : { background: agent.accent }}
      >
        {isUser ? (
          <span className="ml-mono text-[10px] font-semibold text-white">YOU</span>
        ) : (
          <AgentIcon name={agent.icon} size={16} weight="bold" color="#fff" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mb-1 flex items-center gap-2">
          <span>{isUser ? "User" : agent.name}</span>
          {isTruncated && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-[var(--ml-warning)]/20 text-[#8a6d00] border border-[var(--ml-warning)]/40 normal-case tracking-normal">
              <Warning size={11} weight="bold" /> Truncated
            </span>
          )}
        </div>
        {isUser ? (
          <div className="text-[15px] whitespace-pre-wrap text-[var(--ml-text-primary)] leading-relaxed">
            {message.content}
            {message.attachments && message.attachments.length > 0 && (
              <div className="mt-3 space-y-2">
                {message.attachments.map((a) => (
                  <details
                    key={a.name}
                    className="border border-[var(--ml-border)] bg-white"
                  >
                    <summary className="ml-mono text-xs px-3 py-2 cursor-pointer flex items-center gap-2 hover:bg-[var(--ml-surface)]">
                      <FileCode size={14} color="#0f62fe" weight="bold" />
                      {a.name}{" "}
                      <span className="text-[var(--ml-text-secondary)]">
                        · {a.language || "text"}
                      </span>
                    </summary>
                    <pre className="bg-[var(--ml-code-bg)] text-[var(--ml-code-text)] text-xs p-3 overflow-x-auto m-0">
                      {a.content}
                    </pre>
                  </details>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div>
            {objectNote && (
              <div
                className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mb-2 flex flex-wrap gap-x-3 gap-y-1"
                data-testid="object-context-note"
              >
                {objectNote.used && <span data-testid="object-context-used">{objectNote.used}</span>}
                {objectNote.compare && <span data-testid="object-context-compare">{objectNote.compare}</span>}
                {objectNote.ambiguous && (
                  <span className="text-[#8a6d00]" data-testid="object-context-ambiguous">{objectNote.ambiguous}</span>
                )}
              </div>
            )}
            {summary && <CompareSummaryPanel summary={summary} objectContext={message.object_context} createdAt={message.created_at} />}
            {chips && <AmbiguityChips chips={chips} onPick={onPickAmbiguityChip} />}
            <Markdown>{message.content || ""}</Markdown>
            {message.streaming && (
              <span className="inline-flex items-center gap-1 text-sm text-[var(--ml-text-secondary)]">
                {!message.content && <span>Thinking</span>}
                {message.autoResuming && message.content && (
                  <span className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-primary)]">
                    Auto-resuming
                  </span>
                )}
                <span
                  className="inline-block w-2 h-4 bg-[var(--ml-primary)] align-middle ml-0.5 animate-pulse"
                  aria-hidden="true"
                />
              </span>
            )}
            {isTruncated && !message.streaming && onResume && (
              <div className="mt-3 border-t border-[var(--ml-border)] pt-3 flex items-center gap-3">
                <div className="text-xs text-[var(--ml-text-secondary)]">
                  This response was interrupted before completion.
                </div>
                <button
                  onClick={() => onResume(message.id)}
                  disabled={resuming}
                  className="ml-btn-secondary"
                  data-testid={`resume-message-${message.id}`}
                >
                  <ArrowClockwise size={14} weight="bold" />
                  {resuming ? "Resuming…" : "Resume from here"}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
